import { animate, splitText, stagger, cubicBezier } from 'animejs'

const COUNTRIES = [
  'Afghanistan','Albania','Algeria','Andorra','Angola','Antigua and Barbuda',
  'Argentina','Armenia','Australia','Austria','Azerbaijan','Bahamas','Bahrain',
  'Bangladesh','Barbados','Belarus','Belgium','Belize','Benin','Bhutan','Bolivia',
  'Bosnia and Herzegovina','Botswana','Brazil','Brunei','Bulgaria','Burkina Faso',
  'Burundi','Cabo Verde','Cambodia','Cameroon','Canada','Central African Republic',
  'Chad','Chile','China','Colombia','Comoros','Congo','Costa Rica','Croatia','Cuba',
  'Cyprus','Czech Republic','Denmark','Djibouti','Dominica','Dominican Republic',
  'Ecuador','Egypt','El Salvador','Equatorial Guinea','Eritrea','Estonia','Eswatini',
  'Ethiopia','Fiji','Finland','France','Gabon','Gambia','Georgia','Germany','Ghana',
  'Greece','Grenada','Guatemala','Guinea','Guinea-Bissau','Guyana','Haiti','Honduras',
  'Hungary','Iceland','India','Indonesia','Iran','Iraq','Ireland','Israel','Italy',
  'Jamaica','Japan','Jordan','Kazakhstan','Kenya','Kiribati','Kuwait','Kyrgyzstan',
  'Laos','Latvia','Lebanon','Lesotho','Liberia','Libya','Liechtenstein','Lithuania',
  'Luxembourg','Madagascar','Malawi','Malaysia','Maldives','Mali','Malta',
  'Marshall Islands','Mauritania','Mauritius','Mexico','Micronesia','Moldova',
  'Monaco','Mongolia','Montenegro','Morocco','Mozambique','Myanmar','Namibia',
  'Nauru','Nepal','Netherlands','New Zealand','Nicaragua','Niger','Nigeria',
  'North Korea','North Macedonia','Norway','Oman','Pakistan','Palau','Palestine',
  'Panama','Papua New Guinea','Paraguay','Peru','Philippines','Poland','Portugal',
  'Qatar','Romania','Russia','Rwanda','Saint Kitts and Nevis','Saint Lucia',
  'Saint Vincent and the Grenadines','Samoa','San Marino','Sao Tome and Principe',
  'Saudi Arabia','Senegal','Serbia','Seychelles','Sierra Leone','Singapore',
  'Slovakia','Slovenia','Solomon Islands','Somalia','South Africa','South Korea',
  'South Sudan','Spain','Sri Lanka','Sudan','Suriname','Sweden','Switzerland',
  'Syria','Taiwan','Tajikistan','Tanzania','Thailand','Timor-Leste','Togo','Tonga',
  'Trinidad and Tobago','Tunisia','Turkey','Turkmenistan','Tuvalu','Uganda',
  'Ukraine','United Arab Emirates','United Kingdom','United States','Uruguay',
  'Uzbekistan','Vanuatu','Vatican City','Venezuela','Vietnam','Yemen','Zambia',
  'Zimbabwe'
]

class CountryAutocomplete {
  constructor(input) {
    this.input = input
    this.deleting = false
    this.build()
  }

  build() {
    this.input.setAttribute('autocomplete', 'off')

    this.input.addEventListener('keydown', (e) => {
      this.deleting = e.key === 'Backspace' || e.key === 'Delete'

      // Tab or → accepts the inline suggestion
      if (
        (e.key === 'Tab' || e.key === 'ArrowRight') &&
        this.input.selectionStart < this.input.selectionEnd
      ) {
        e.preventDefault()
        const end = this.input.value.length
        this.input.setSelectionRange(end, end)
      }
    })

    this.input.addEventListener('input', () => this.onInput())
  }

  onInput() {
    if (this.deleting) return

    const q = this.input.value
    if (!q) return

    const match = COUNTRIES.find(c => c.toLowerCase().startsWith(q.toLowerCase()))
    if (!match) return

    this.input.value = match
    this.input.setSelectionRange(q.length, match.length)

    this.input.classList.remove('has-suggestion')
    this.input.classList.add('has-suggestion')
  }
}

// ---------------------------------------------------------------------------
// Custom multiselect — replaces <select multiple> with a styled checkbox list.
// Add the CSS class "custom-multiselect" to the field in Formidable's
// "Custom CSS class" field setting to activate it.
// ---------------------------------------------------------------------------
class CustomMultiselect {
  constructor(select) {
    this.select      = select
    this.placeholder = [...select.options].find(o => !o.value)?.textContent.trim() || 'Select…'
    this.build()
    this.bindClose()
  }

  build() {
    this.select.style.display = 'none'

    this.root = document.createElement('div')
    this.root.className = 'cms-root'

    // Trigger
    this.trigger = document.createElement('button')
    this.trigger.type = 'button'
    this.trigger.className = 'cms-trigger'
    this.trigger.setAttribute('aria-expanded', 'false')
    this.trigger.addEventListener('click', e => { e.stopPropagation(); this.toggle() })

    this.triggerLabel = document.createElement('span')
    this.triggerLabel.className = 'cms-label'
    this.trigger.append(this.triggerLabel)
    this.trigger.insertAdjacentHTML('beforeend', '<span class="cms-arrow" aria-hidden="true"></span>')

    // Dropdown panel
    this.dropdown = document.createElement('div')
    this.dropdown.className = 'cms-dropdown'
    this.dropdown.setAttribute('data-lenis-prevent', '')

    const list = document.createElement('ul')
    list.className = 'cms-list'

    ;[...this.select.options].forEach(opt => {
      if (!opt.value) return // skip placeholder

      const isOther = opt.classList.contains('frm_other_trigger')
      const li      = document.createElement('li')
      li.className  = 'cms-item' + (isOther ? ' cms-item--other' : '')

      const label   = document.createElement('label')
      label.className = 'cms-item-label'

      const cb      = document.createElement('input')
      cb.type       = 'checkbox'
      cb.value      = opt.value
      cb.className  = 'cms-cb'

      const text    = document.createElement('span')
      text.className = 'cms-item-text'
      text.textContent = opt.textContent.trim()

      cb.addEventListener('change', () => {
        this.syncToSelect()
        this.updateLabel()
      })

      label.append(cb, text)
      li.appendChild(label)
      list.appendChild(li)
    })

    this.dropdown.appendChild(list)
    this.root.append(this.trigger, this.dropdown)
    this.select.insertAdjacentElement('afterend', this.root)
    this.updateLabel()
  }

  toggle() {
    const open = this.root.classList.toggle('is-open')
    this.trigger.setAttribute('aria-expanded', String(open))
  }

  close() {
    this.root.classList.remove('is-open')
    this.trigger.setAttribute('aria-expanded', 'false')
  }

  bindClose() {
    document.addEventListener('click', e => {
      if (!this.root.contains(e.target)) this.close()
    })
  }

  // Sync checked checkboxes → native select, then fire change so Formidable's
  // JS (including the "Other" text-input toggle) picks it up.
  syncToSelect() {
    const checked = new Set([...this.root.querySelectorAll('.cms-cb:checked')].map(cb => cb.value))
    ;[...this.select.options].forEach(opt => { opt.selected = checked.has(opt.value) })
    this.select.dispatchEvent(new Event('change', { bubbles: true }))
    if (typeof jQuery !== 'undefined') jQuery(this.select).trigger('change')
  }

  updateLabel() {
    const checked = [...this.root.querySelectorAll('.cms-cb:checked')]
    if (!checked.length) {
      this.triggerLabel.textContent = this.placeholder
      this.triggerLabel.classList.add('is-placeholder')
    } else {
      this.triggerLabel.textContent = checked
        .map(cb => [...this.select.options].find(o => o.value === cb.value)?.textContent.trim() ?? cb.value)
        .join(', ')
      this.triggerLabel.classList.remove('is-placeholder')
    }
  }
}

export class ContactForm {
  constructor() {
    this.wrapper = document.querySelector('.template-contact')
    if (!this.wrapper) return
    this.init()
  }

  init() {
    this.initCountryAutocomplete()
    this.initPhoneField()
    this.initUrlPreFill()
    this.initTimepieceSought()
    this.initMultiselect()

    // Formidable fires its events via jQuery — bail gracefully if it hasn't loaded yet.
    if (typeof jQuery === 'undefined') return

    jQuery(document).off('frmFormComplete.bovet')

    jQuery(document).on('frmFormComplete.bovet', (_e, form) => {
      this.animateLotus()
      this.animateSuccessMessage(form)
    })
  }


  // Reads the session list of viewed timepieces and pre-fills the hidden timepiece_sought field.
  // Add the CSS class "timepiece-sought" to the field in Formidable's "Custom CSS class" setting.
  initTimepieceSought() {
    const raw = JSON.parse(sessionStorage.getItem('bovet_viewed_timepieces') || '{}')
    const counts = Array.isArray(raw) ? {} : raw
    if (!Object.keys(counts).length) return

    const last = sessionStorage.getItem('bovet_last_timepiece') || ''

    const value = Object.entries(counts)
      .sort(([, a], [, b]) => b - a)           // most visited first
      .map(([title, count]) => {
        const tags = []
        if (count > 1) tags.push(`${count} visits`)
        if (title === last) tags.push('last visited')
        return tags.length ? `${title} (${tags.join(', ')})` : title
      })
      .join(', ')

    const field = this.wrapper.querySelector(
      '#field_timepiece_sought, .timepiece-sought input, .timepiece-sought textarea'
    )
    if (!field) return
    field.value = value
    field.dispatchEvent(new Event('change', { bubbles: true }))
  }

  // Replaces the flag in the intl-tel-input selected button with the ISO2 country code (e.g. "CA").
  // Flags render as rectangles on Windows so we hide them via CSS and show the code instead.
  // Also adds data-lenis-prevent to the dropdown so Lenis doesn't block native scrolling inside it.
  initPhoneField() {
    this.wrapper.querySelectorAll('input[type="tel"]').forEach(input => {
      if (input.dataset.phoneReady) return
      input.dataset.phoneReady = 'true'

      if (!input.closest('.iti')) return

      // Formidable Pro's own script can call intlTelInput() on this input twice
      // (once on load, once again from its frmPageChanged handler) without checking
      // whether it's already wrapped. intl-tel-input then nests a second, live
      // .iti wrapper inside the first, leaving the original's flag+arrow behind as
      // a dead, unlabeled duplicate. Strip any such orphaned wrapper so only the
      // innermost (live) one — the one actually containing the input — remains.
      const cleanupOrphans = () => {
        let live = input.closest('.iti')
        while (live?.parentElement?.classList.contains('iti')) {
          const outer = live.parentElement
          outer.querySelectorAll(':scope > .iti__country-container').forEach(el => el.remove())
          live = outer
        }
      }

      // Try the iti API, fall back to reading the iti__XX class from the flag element.
      // Note: method is getSelectedCountry() in newer versions, getSelectedCountryData() in older.
      const getIso2 = () => {
        const itiWrapper = input.closest('.iti')
        const iti = window.intlTelInputGlobals?.getInstance(input)
        if (iti) {
          const data = iti.getSelectedCountry?.() ?? iti.getSelectedCountryData?.()
          const code = data?.iso2 ?? data?.countryCode ?? ''
          if (code) return code.toUpperCase()
        }
        // DOM fallback: flag element carries a class like iti__ca alongside iti__flag
        const flag = itiWrapper?.querySelector('.iti__flag')
        if (!flag) return ''
        const cls = [...flag.classList].find(c => c !== 'iti__flag' && c.startsWith('iti__'))
        return cls?.replace('iti__', '').toUpperCase() ?? ''
      }

      const update = () => {
        cleanupOrphans()
        const itiWrapper = input.closest('.iti')
        const iso2 = getIso2()
        if (!iso2 || !itiWrapper) return
        // Structure: .iti__selected-country > .iti__selected-country-primary > .iti__flag + .iti__selected-dial-code
        //            .iti__selected-country > .iti__arrow  (sibling of primary, not inside it)
        const primary = itiWrapper.querySelector('.iti__selected-country-primary')
        const btn     = primary ?? itiWrapper.querySelector('.iti__selected-country')
        if (!btn) return
        let label = btn.querySelector('.iti__country-code-label')
        if (!label) {
          label = document.createElement('span')
          label.className = 'iti__country-code-label'
          // Append after the (hidden) flag, before the dial code
          const dialCode = btn.querySelector('.iti__selected-dial-code')
          dialCode ? btn.insertBefore(label, dialCode) : btn.appendChild(label)
        }
        label.textContent = iso2
      }

      // The dropdown is appended to document.body as .iti--container, outside the .iti wrapper.
      // We stop wheel propagation directly on the list so Lenis never receives the event,
      // and also set data-lenis-prevent as a belt-and-suspenders fallback.
      // Uses a MutationObserver because the container may not exist until first open.
      const fixLenis = () => {
        const attach = (list) => {
          if (list.dataset.lenisFixed) return
          list.dataset.lenisFixed = 'true'
          list.setAttribute('data-lenis-prevent', '')
          list.addEventListener('wheel', (e) => e.stopPropagation(), { passive: false })
          list.addEventListener('touchmove', (e) => e.stopPropagation(), { passive: false })
        }

        const existing = document.querySelector('.iti--container .iti__country-list')
                      ?? document.querySelector('.iti__country-list')
        if (existing) { attach(existing); return }

        // Watch for the container being inserted into the body on first open
        const observer = new MutationObserver(() => {
          const list = document.querySelector('.iti--container .iti__country-list')
                    ?? document.querySelector('.iti__country-list')
          if (list) { attach(list); observer.disconnect() }
        })
        observer.observe(document.body, { childList: true, subtree: true })
      }

      // iti initialises asynchronously — poll until the instance and flag class are ready
      const tryInit = () => {
        if (getIso2()) {
          update()
          fixLenis()
          input.addEventListener('countrychange', update)

          // Formidable's frmPageChanged handler can trigger the double-init (see
          // cleanupOrphans above) at any point after this, even well after iti is
          // ready. Watch the field for the resulting DOM shuffle and clean it up.
          const field = input.closest('.frm_form_field') ?? input.parentElement
          if (field) {
            new MutationObserver(cleanupOrphans).observe(field, { childList: true, subtree: true })
          }
        } else {
          setTimeout(tryInit, 100)
        }
      }
      tryInit()
    })
  }

  initUrlPreFill() {
    const timepiece = new URLSearchParams(window.location.search).get('timepiece')
    if (!timepiece) return

    const fill = () => {
      const input = document.getElementById('field_timepiece_interest')
      if (!input) return
      const ta = document.createElement('textarea')
      ta.innerHTML = timepiece
      input.value = ta.value
      input.dispatchEvent(new Event('input', { bubbles: true }))
      input.dispatchEvent(new Event('change', { bubbles: true }))
    }

    // Small delay to let Formidable finish its own DOM setup
    setTimeout(fill, 100)
  }

  // Replaces every <select multiple> inside a field with class "custom-multiselect"
  // with a styled checkbox-dropdown widget.
  // Add that CSS class to the field in Formidable's "Custom CSS class" setting.
  initMultiselect() {
    this.wrapper.querySelectorAll('.custom-multiselect select').forEach(select => {
      if (select.dataset.msReady) return
      select.dataset.msReady = 'true'
      new CustomMultiselect(select)
    })
  }

  // Looks for any input inside a field with the class "country-autocomplete".
  // Add that CSS class to the field in Formidable's "Custom CSS class" setting.
  initCountryAutocomplete() {
    this.wrapper.querySelectorAll('.country-autocomplete input').forEach(input => {
      if (input.dataset.autocompleteReady) return
      input.dataset.autocompleteReady = 'true'
      new CountryAutocomplete(input)
    })
  }

  animateSuccessMessage(form) {
    const formId = form?.querySelector?.('input[name="form_id"]')?.value
    const container = (formId && document.querySelector(`#frm_form_${formId}_container`))
      || this.wrapper.querySelector('.frm_forms')
    if (!container) return

    container.querySelectorAll('.anime-hero-title').forEach(el => {
      // Already split on a previous run — just re-animate the chars
      if (el.dataset.animeReady) {
        const chars = [...el.querySelectorAll('span > span')]
        if (chars.length) {
          animate(chars, {
            opacity: [0, 0, 1],
            y: ['110%', '0%'],
            delay: stagger(25),
            duration: 1000,
            ease: cubicBezier(0.567, -0.013, 0.493, 0.98),
          })
        }
        return
      }

      el.dataset.animeReady = 'true'
      splitText(el, { chars: {} }).addEffect(({ chars }) => {
        el.style.opacity = '1'
        chars.forEach(char => {
          const clip = document.createElement('span')
          clip.style.cssText = 'display:inline-block;overflow:hidden;vertical-align:bottom;'
          char.parentNode.insertBefore(clip, char)
          clip.appendChild(char)
          char.style.display = 'inline-block'
        })
        animate(chars, {
          opacity: [0, 0, 1],
          y: ['110%', '0%'],
          delay: stagger(25),
          duration: 1000,
          ease: cubicBezier(0.567, -0.013, 0.493, 0.98),
        })
      })
    })
  }

  animateLotus() {
    const lotus = this.wrapper.querySelector('.bg_lotus')
    if (!lotus) return
    animate(lotus, {
      rotate: '+=10',
      duration: 500,
      opacity: 0,
      scale: 1.1,
      ease: 'inSine',
      onComplete: () => {
        animate(lotus, {
          rotate: '+=10',
          duration: 600,
          opacity: [0, 1],
          scale: [1.1, 1],
          ease: 'outSine',
        })
      }
    })
  }
}
